import React, { useCallback, useMemo } from "react";

import { GroceryItem, weeksApi } from "@/client/api";
import { GroceryItemRow } from "@/client/components/GroceryItemRow";
import { SideDrawer } from "@/client/components/SideDrawer";
import { TypeSection } from "@/client/components/TypeSection";
import { Spinner } from "@/client/components/ui";
import { buildDayClipboard } from "@/client/computations/grocery";
import { INGREDIENT_TYPE_EMOJIS, INGREDIENT_TYPE_LABELS } from "@/client/constants";
import { useToast } from "@/client/context/ToastContext";
import { useApi } from "@/client/hooks/useApi";
import { useCyclingIndex } from "@/client/hooks/useCyclingIndex";
import { useGroceryChecks } from "@/client/hooks/useGroceryChecks";
import { CopyIcon, GroceryIcon } from "@/client/icons";
import { DAYS_ES_FULL } from "@/client/pages/plan/types";

export function WeekGroceryDrawer({ weekId, onClose }: { weekId: string; onClose: () => void }) {
  const { toast } = useToast();
  const fetcher = useCallback(() => weeksApi.groceryList(weekId), [weekId]);
  const { data, loading } = useApi(fetcher, [weekId]);

  const [repIndex, cycleRep] = useCyclingIndex();
  const checksApi = useMemo(
    () => ({
      list: () => weeksApi.groceryChecks(weekId),
      set: (key: string, checked: boolean) => weeksApi.setGroceryCheck(weekId, key, checked),
    }),
    [weekId],
  );
  const [checked, toggleChecked] = useGroceryChecks(checksApi);

  const hasItems = data && data.days.length > 0;

  function clipboard(day: string, items: Record<string, GroceryItem[]>): string {
    return buildDayClipboard(
      day,
      DAYS_ES_FULL[day] ?? day,
      items,
      repIndex,
      INGREDIENT_TYPE_EMOJIS,
      INGREDIENT_TYPE_LABELS,
    );
  }

  function copyDay(day: string, items: Record<string, GroceryItem[]>) {
    navigator.clipboard.writeText(clipboard(day, items));
    toast(`${DAYS_ES_FULL[day]} copiado`);
  }

  function copyAll() {
    if (!data) return;
    navigator.clipboard.writeText(
      data.days.map(({ day, items }) => clipboard(day, items)).join("\n\n"),
    );
    toast("Lista copiada");
  }

  return (
    <SideDrawer
      title="Ingredientes de la semana"
      icon={
        <GroceryIcon width={20} height={20} className="text-term-accent shrink-0" aria-hidden />
      }
      onClose={onClose}
      toolbar={
        <>
          <span className="text-xs text-term-text-muted">
            Ingredientes del día de cada receta planificada
          </span>
          {!loading && hasItems && (
            <button
              onClick={copyAll}
              className="text-sm text-term-text-muted hover:text-term-accent cursor-pointer transition-colors flex items-center gap-1"
            >
              <CopyIcon width={14} height={14} className="shrink-0" aria-hidden />
              Copiar todo
            </button>
          )}
        </>
      }
    >
      {loading && <Spinner className="!py-6" />}
      {!loading && !hasItems && (
        <p className="text-sm text-term-text-muted text-center py-6">
          Sin ingredientes para esta semana.
        </p>
      )}
      {!loading && hasItems && (
        <div className="flex flex-col">
          {data!.days.map(({ day, items }) => (
            <div
              key={day}
              className="border-t border-term-border py-3 first:border-t-0 first:pt-0 last:pb-0"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-term-accent uppercase tracking-wide">
                  {DAYS_ES_FULL[day] ?? day}
                </span>
                <button
                  onClick={() => copyDay(day, items)}
                  className="text-micro text-term-text-muted hover:text-term-accent cursor-pointer transition-colors flex items-center gap-0.5"
                >
                  <CopyIcon width={11} height={11} className="shrink-0" aria-hidden />
                  Copiar
                </button>
              </div>
              {Object.entries(items).map(([type, typeItems]) => (
                <TypeSection
                  key={type}
                  emoji={INGREDIENT_TYPE_EMOJIS[type] ?? "📦"}
                  label={INGREDIENT_TYPE_LABELS[type] ?? type}
                >
                  {(typeItems as GroceryItem[]).map((item, i) => (
                    <GroceryItemRow
                      key={i}
                      item={item}
                      repIndex={repIndex[item.ingredient_id] ?? 0}
                      isChecked={checked.has(`${item.ingredient_id}:${day}`)}
                      onToggle={() => toggleChecked(`${item.ingredient_id}:${day}`)}
                      onCycleRep={() => cycleRep(item.ingredient_id, item.representations.length)}
                    />
                  ))}
                </TypeSection>
              ))}
            </div>
          ))}
        </div>
      )}
    </SideDrawer>
  );
}
